import React from "react";

import GroupWorkIcon from "@material-ui/icons/GroupWork";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import { Button, Divider, Popover, Select, Space } from "antd";
import styled from "styled-components/macro";
import { applyFieldNameMode } from "utils/column/getColumnLabelWithContext";

import { EntityKind } from "models/entityKind";
import { AggregationType, IGroupByMap } from "models/groupBy";

import { LabeledSwitch } from "components/base";
import BinSettings from "components/filter/BinSettings";
import GroupBy from "components/groupBy/GroupBy";

import { useGroupModeController } from "./hooks/useGroupModeController";

const { Option } = Select;

interface GroupModePopoverProps {
  entityKind: EntityKind;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface StyledButtonProps {
  $isGroupMode: boolean;
}

const GroupModePopover: React.FC<GroupModePopoverProps> = ({
  entityKind,
  open,
  onOpenChange
}) => {
  const {
    groupBy,
    isGroupMode,
    modeDataType,
    localBin,
    isFetchingBinDefaults,
    binFetchError,
    binFetchUnsupported,
    pendingModeField,
    handleToggleGroupMode,
    handleFieldChange,
    handleAggregationChange,
    handleBinChange,
    handleBinSettingChange,
    handleBinReset,
    handleBinLock
  } = useGroupModeController(entityKind);

  // BinSettings are driven by the GROUP field's type (outer property).
  const isNumericGroupField = modeDataType === "Number" || modeDataType === "Integer";

  // Aggregation options are driven by the FOCUS field's type (inner property),
  // because the aggregation tells the backend how to collapse focus-field values
  // within each group. e.g. group by MD (numeric) focused on Operator (text)
  // → show text aggregations (MostCommon, JoinDistinct).
  const isFocusNumeric =
    groupBy?.dataType === "Number" || groupBy?.dataType === "Integer";

  // EVA-127: "Focus Field" is a disambiguation surface (two custom fields can
  // share a name across different subgroups), never a screenshot, so resolve
  // with the not-in-screenshot mode (`undefined`) rather than raw title/property.
  const focusFieldName =
    applyFieldNameMode(groupBy?.title, groupBy, undefined) || groupBy?.property;

  const numericAggregations: AggregationType[] = [
    AggregationType.Default,
    AggregationType.Sum,
    AggregationType.Average,
    AggregationType.Min,
    AggregationType.Max
  ];

  const textAggregations: AggregationType[] = [
    AggregationType.Default,
    AggregationType.MostCommon,
    AggregationType.JoinDistinct
  ];

  const aggregationOptions = isFocusNumeric ? numericAggregations : textAggregations;

  const content = (
    <Wrapper>
      <StyledSpace direction="vertical" split={<StyledDivider />}>
        <LabeledSwitch
          testId="group-mode-toggle"
          switch={{
            isChecked: isGroupMode,
            onChange: (checked) => {
              if (!checked) {
                onOpenChange?.(false);
              }
              handleToggleGroupMode(checked);
            }
          }}
          label={{
            value: "Group Mode",
            isBold: true,
            isUppercase: true
          }}
        />
        {isGroupMode && (
          <SettingsSection>
            {/* ── Group Field ─────────────────────────────────── */}
            <Row>
              <FieldColumn>
                <Label>Group Field</Label>
                <GroupBy
                  entityKind={entityKind}
                  value={
                    {
                      property:
                        pendingModeField?.property ?? groupBy.mode?.property ?? "",
                      title:
                        pendingModeField?.title ??
                        groupBy.mode?.title ??
                        groupBy.mode?.property ??
                        ""
                    } as IGroupByMap
                  }
                  onChange={handleFieldChange}
                  placement="rightTop">
                  <FieldButton block>
                    <span>
                      {pendingModeField?.title ??
                        groupBy.mode?.title ??
                        groupBy.mode?.property ??
                        "Select Field"}
                    </span>
                    <KeyboardArrowRightIcon style={{ fontSize: 24 }} />
                  </FieldButton>
                </GroupBy>
              </FieldColumn>
            </Row>

            {/* ── Bin Settings (associated with group field) ──── */}
            {isNumericGroupField && (
              <BinSettingsWrapper>
                {binFetchUnsupported && (
                  <BinFetchErrorNotice>
                    Bin settings are not available when the outer group property uses
                    numeric binning (Phase 1 limitation).
                  </BinFetchErrorNotice>
                )}
                {binFetchError && (
                  <BinFetchErrorNotice>
                    Could not load defaults — review values before continuing.
                  </BinFetchErrorNotice>
                )}
                <BinSettings
                  bin={localBin}
                  dataType={modeDataType}
                  entityKind={entityKind}
                  canEditBin={!isFetchingBinDefaults}
                  onBinChange={handleBinChange}
                  onBinSettingChange={handleBinSettingChange}
                  onReset={handleBinReset}
                  onLock={handleBinLock}
                  groupByPropertyName={
                    pendingModeField?.property ?? groupBy.mode?.property
                  }
                />
              </BinSettingsWrapper>
            )}

            {/* ── Focus Field aggregation ──────────────────────── */}
            <InnerDivider />
            <FocusSection>
              <Label>Focus Field</Label>
              <FocusAggregationRow>
                {/* EVA-127: not-in-screenshot mode (subgroup-qualified, never
                    the category) — this is a disambiguation surface, so two
                    custom fields sharing a name in different subgroups must
                    read differently here. */}
                <FocusFieldName title={focusFieldName}>{focusFieldName}</FocusFieldName>
                <AggregatedByText>aggregated by</AggregatedByText>
                <AggregationSelect
                  data-testid="aggregation-select"
                  value={
                    pendingModeField?.aggregation ??
                    groupBy.mode?.aggregation ??
                    AggregationType.Default
                  }
                  onChange={handleAggregationChange}>
                  {aggregationOptions.map((agg) => (
                    <Option key={agg} value={agg}>
                      {aggregationLabel(agg)}
                    </Option>
                  ))}
                </AggregationSelect>
              </FocusAggregationRow>
            </FocusSection>
          </SettingsSection>
        )}
      </StyledSpace>
    </Wrapper>
  );

  return (
    <Popover
      content={content}
      overlayClassName="group-mode-popover"
      trigger="click"
      placement="bottom"
      open={open}
      onOpenChange={onOpenChange}>
      <StyledIconButton
        data-testid="group-mode-toggle-button"
        icon={<StyledIcon />}
        $isGroupMode={isGroupMode}
      />
    </Popover>
  );
};

export default GroupModePopover;

const AGGREGATION_LABELS: Record<AggregationType, string> = {
  [AggregationType.Default]: "Default",
  [AggregationType.Sum]: "Sum",
  [AggregationType.Average]: "Average",
  [AggregationType.Min]: "Min",
  [AggregationType.Max]: "Max",
  [AggregationType.MostCommon]: "Most Common",
  [AggregationType.JoinDistinct]: "Join Distinct"
};

function aggregationLabel(agg: AggregationType): string {
  return AGGREGATION_LABELS[agg] ?? agg;
}

const Wrapper = styled.div`
  width: 380px;
  max-width: 380px;
  background: var(--normalize-section-bg, #ffffff);
  font-weight: 500;
  padding: 20px;
  text-align: left;
`;

const StyledDivider = styled(Divider)`
  margin: 0;
`;

const StyledSpace = styled(Space)`
  width: 100%;
`;

const SettingsSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 5%;
  margin-bottom: 10px;
`;

const FieldColumn = styled.div`
  width: 100%;
`;

const BinSettingsWrapper = styled.div`
  /* Strip the horizontal padding from the inner BinSettingsContent container
     so the 4-column input grid has enough room and "Greater than" fits on
     a single line. Also remove the fixed min-height to avoid blank space. */
  & > div {
    min-height: unset;
  }
  & > div > div {
    padding-left: 0;
    padding-right: 0;
  }
`;

const Label = styled.span`
  white-space: break-spaces;
  text-overflow: ellipsis;
  overflow: hidden;
  display: block;
  margin-top: 0;
`;

const FieldButton = styled(Button)`
  width: 100%;
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  height: 30px;
  text-align: left;

  & > span {
    max-width: fit-content;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const StyledIcon = styled(GroupWorkIcon)`
  font-size: 18px !important;
`;

const StyledIconButton = styled(Button)<StyledButtonProps>`
  background: ${({ $isGroupMode }) =>
    $isGroupMode ? "var(--color-primary)" : "transparent"};
  font-size: 1.3rem;
  color: ${({ $isGroupMode }) => ($isGroupMode ? "white" : "#a2aaad")};
  display: flex;
  align-items: center;

  &:hover {
    color: ${({ $isGroupMode }) =>
      $isGroupMode ? "var(--color-primary)" : "var(--color-text)"};
  }
`;

const InnerDivider = styled.div`
  border-top: 1px solid var(--color-divider, #e8e8e8);
  margin: 10px 0;
`;

const FocusSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FocusAggregationRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
`;

const FocusFieldName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
`;

const AggregatedByText = styled.span`
  flex-shrink: 0;
  color: var(--color-text-muted, #a2aaad);
  font-size: 1.1rem;
  white-space: nowrap;
`;

const AggregationSelect = styled(Select)`
  flex-shrink: 0;
  width: 130px;
  height: 30px;
` as typeof Select;

// Amber inline notice — matches the app's existing inline-error visual weight.
const BinFetchErrorNotice = styled.div`
  font-size: 11px;
  color: #d97706; /* amber-600 */
  padding: 4px 0 2px;
`;
